import classNames from 'classnames';
import { ChangeEvent, FC, ReactNode, useCallback, useEffect, useState } from 'react';

import { FieldMessage, Flex, Icon, Listbox, SearchField, Text } from '@servicetitan/anvil2';
import IconArrowForwardIos from '@servicetitan/anvil2/assets/icons/material/round/arrow_forward_ios.svg';
import { Modal, Spinner } from '@servicetitan/web-feature-ui';

import * as styles from './single-select.module.css';

export interface SingleSelectOption {
    id: string | number;
    label: string;
    disabled?: boolean;
}

export interface SingleSelectProps {
    /**
     * Array of options to display in the select dropdown
     */
    items: SingleSelectOption[];
    /**
     * Currently selected item
     */
    selectedItem?: SingleSelectOption | null;
    /**
     * Callback fired when selection changes
     */
    onChange?: (item: SingleSelectOption | null) => void;
    /**
     * Default selected item (for uncontrolled usage)
     */
    defaultSelectedItem?: SingleSelectOption;
    /**
     * Label text displayed above the select
     */
    label?: string;
    /**
     * Placeholder text displayed when no item is selected
     */
    placeholder?: string;
    /**
     * Hint text displayed below the select
     */
    hint?: string;
    /**
     * Description text displayed below the select
     */
    description?: string;
    /**
     * Error message to display
     */
    error?: string;
    /**
     * Whether the select is disabled
     */
    disabled?: boolean;
    /**
     * Whether the field is required
     */
    required?: boolean;
    /**
     * Title for the bottom sheet modal
     */
    modalTitle?: string;
    /**
     * Initial breakpoint for the bottom sheet modal (0 to 1)
     * @default 0.5
     */
    initialBreakpoint?: number;
    /**
     * Whether the select supports search functionality
     * @default false
     */
    searchable?: boolean;
    /**
     * Placeholder text for the search input
     * @default 'Search...'
     */
    searchPlaceholder?: string;
    /**
     * Whether items are currently being loaded
     * @default false
     */
    isLoading?: boolean;
    /**
     * Callback fired when search query changes.
     * Use this for server-side filtering/fetching.
     */
    onSearch?: (query: string) => void;
    /**
     * Debounce delay in milliseconds for the onSearch callback
     * @default 300
     */
    searchDebounceMs?: number;
    /**
     * Title for the empty state when no items match the search
     * @default 'No Results Found'
     */
    emptyStateTitle?: string;
    /**
     * Description for the empty state when no items match the search
     */
    emptyStateDescription?: string;
    /**
     * Custom icon/illustration to display in the empty state
     */
    emptyStateIcon?: ReactNode;
    /**
     * Whether to filter items locally based on search query.
     * Set to false when doing server-side filtering.
     * @default true
     */
    filterLocally?: boolean;
    /**
     * Minimum characters required before triggering search
     * @default 0
     */
    minSearchLength?: number;
}

/**
 * SingleSelect component for selecting a single option from a dropdown list.
 * Opens a bottom sheet modal on mobile devices when clicked.
 * Supports searchable options, loading states, and async data fetching.
 * Based on Anvil2 components and web-feature-ui Modal for bottom sheet behavior.
 */
export const SingleSelect: FC<SingleSelectProps> = ({
    items,
    selectedItem: controlledSelectedItem,
    onChange,
    defaultSelectedItem,
    label,
    placeholder = 'Placeholder',
    hint,
    description,
    error,
    disabled = false,
    required = false,
    modalTitle,
    initialBreakpoint = 0.5,
    searchable = false,
    searchPlaceholder = 'Search...',
    isLoading = false,
    onSearch,
    searchDebounceMs = 300,
    emptyStateTitle = 'No Results Found',
    emptyStateDescription,
    emptyStateIcon,
    filterLocally = true,
    minSearchLength = 0,
}) => {
    const [uncontrolledSelectedItem, setUncontrolledSelectedItem] =
        useState<SingleSelectOption | null>(defaultSelectedItem || null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

    const isControlled = controlledSelectedItem !== undefined;
    const selectedItem = isControlled ? controlledSelectedItem : uncontrolledSelectedItem;

    // Debounce search query for onSearch callback
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, searchDebounceMs);

        return () => clearTimeout(timer);
    }, [searchQuery, searchDebounceMs]);

    // Trigger onSearch callback when debounced query changes
    useEffect(() => {
        if (onSearch && debouncedSearchQuery.length >= minSearchLength) {
            onSearch(debouncedSearchQuery);
        }
    }, [debouncedSearchQuery, onSearch, minSearchLength]);

    // Filter items locally if enabled
    const filteredItems =
        filterLocally && searchQuery.length >= minSearchLength
            ? items.filter(item => item.label.toLowerCase().includes(searchQuery.toLowerCase()))
            : items;

    const handleChange = useCallback(
        (item: SingleSelectOption | null) => {
            if (!isControlled) {
                setUncontrolledSelectedItem(item);
            }
            onChange?.(item);
            setIsModalOpen(false);
            setSearchQuery('');
        },
        [isControlled, onChange]
    );

    const handleTriggerClick = () => {
        if (!disabled) {
            setIsModalOpen(true);
        }
    };

    const handleModalDismiss = () => {
        setIsModalOpen(false);
        setSearchQuery('');
    };

    const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    };

    const handleSearchClear = () => {
        setSearchQuery('');
    };

    const displayText = selectedItem ? selectedItem.label : placeholder;
    const hasError = !!error;
    const isPlaceholder = !selectedItem;
    const showEmptyState =
        !isLoading && filteredItems.length === 0 && searchQuery.length >= minSearchLength;

    const triggerClassName = classNames(styles.trigger, {
        [styles.disabled]: disabled,
        [styles.error]: hasError,
    });

    const displayTextClassName = classNames(styles.displayText, {
        [styles.placeholder]: isPlaceholder && !hasError,
        [styles.disabled]: disabled,
        [styles.errorPlaceholder]: hasError && isPlaceholder,
    });

    const renderLoadingState = () => (
        <Flex className="a2-p-4" justifyContent="center">
            <Spinner size="2.75rem" />
        </Flex>
    );

    const renderEmptyState = () => (
        <Flex direction="column" alignItems="center" justifyContent="center" className="a2-p-6">
            {emptyStateIcon && (
                <Flex alignItems="center" justifyContent="center" className="a2-m-block-end-6">
                    {emptyStateIcon}
                </Flex>
            )}
            <Text el="h6" variant="headline">
                {emptyStateTitle}
            </Text>
            {emptyStateDescription && (
                <Text variant="body" subdued className={styles.emptyStateDescription}>
                    {emptyStateDescription}
                </Text>
            )}
        </Flex>
    );

    const renderContent = () => {
        if (isLoading) {
            return renderLoadingState();
        }

        if (showEmptyState) {
            return renderEmptyState();
        }

        return (
            <Listbox
                selected={selectedItem?.label}
                onSelectionChange={selectedLabel => {
                    const selectedOption = filteredItems.find(item => item.label === selectedLabel);
                    handleChange(selectedOption || null);
                }}
            >
                {filteredItems.map(item => (
                    <Listbox.Option key={item.id} label={item.label} disabled={item.disabled}>
                        {item.label}
                    </Listbox.Option>
                ))}
            </Listbox>
        );
    };

    return (
        <Flex direction="column" className={styles.container}>
            {label && (
                <Flex gap="1" alignItems="center">
                    <Text
                        variant="body"
                        size="small"
                        className={classNames(
                            'a2-fw-label',
                            styles.labelText,
                            hasError ? 'a2-c-danger' : 'a2-c-default'
                        )}
                    >
                        {label}
                    </Text>
                    {required && (
                        <Text
                            variant="body"
                            size="small"
                            className={classNames('a2-fw-label', 'a2-c-danger', styles.labelText)}
                        >
                            *
                        </Text>
                    )}
                </Flex>
            )}

            <Flex
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                role="button"
                tabIndex={disabled ? -1 : 0}
                onClick={handleTriggerClick}
                onKeyDown={e => {
                    if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
                        e.preventDefault();
                        handleTriggerClick();
                    }
                }}
                className={triggerClassName}
            >
                <Text variant="body" size="medium" className={displayTextClassName}>
                    {displayText}
                </Text>
                <Icon size="small" svg={IconArrowForwardIos} className={styles.triggerIcon} />
            </Flex>

            {(hint || description || error) && (
                <FieldMessage hint={hint} description={description} errorMessage={error} />
            )}

            <Modal
                isOpen={isModalOpen}
                onDidDismiss={handleModalDismiss}
                breakpoints={[0, 0.5, 0.75, 1]}
                initialBreakpoint={initialBreakpoint}
                handle
            >
                <Modal.Header>{modalTitle || label || 'Select an option'}</Modal.Header>
                <Modal.Content className="a2-p-inline-0 a2-p-block-0">
                    <Flex direction="column">
                        {searchable && (
                            <Flex className={classNames('a2-p-4', styles.searchContainer)}>
                                <SearchField
                                    placeholder={searchPlaceholder}
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                    onClear={handleSearchClear}
                                    size="large"
                                    aria-label={`Search ${modalTitle || label || 'options'}`}
                                    className={styles.searchField}
                                />
                            </Flex>
                        )}
                        {renderContent()}
                    </Flex>
                </Modal.Content>
            </Modal>
        </Flex>
    );
};
